import { useNavigate, useParams } from 'react-router-dom';
import React, { useEffect, useRef, useState } from 'react';

import * as Yup from 'yup';
import { isEqual } from 'lodash';
import { useFormik } from 'formik';
import { useQueryClient } from '@tanstack/react-query';
import { useRecoilState, useRecoilValue } from 'recoil';

import { requestApi } from 'api/Api';
import { getPermissions } from 'api/Permissions';

import state from 'components/state';
import { showAlert } from 'components/shared/Alert/Alert';

import { EditRoleContext } from './EditRoleContext';

const EditRoleValidationSchema = Yup.object().shape({
  role_name: Yup.string().required('Role name is required')
});

export const EditRoleContextProvider = ({ children, forAdmin }) => {
  const [showEditRoleModal, setShowEditRoleModal] = useState(false);
  const [enableSubmit, setEnableSubmit] = useState(false);
  const [actionsEnabled, setActionsEnabled] = useState({});
  const userState = useRecoilValue(state.userState);
  // eslint-disable-next-line no-unused-vars
  const [userPermissions, setUserPermissions] = useRecoilState(state.permissions);
  const currentUserId = userState.user_id;
  const navigate = useNavigate();
  const [actionDependents, setActionDependents] = useState({});
  const [dependencyOfModal, setDependencyOfModal] = useState([]);
  const [actionDependencies, setActionDependencies] = useState({});
  const [showModal, setShowModal] = useState(false);

  const queryClient = useQueryClient();

  const formik = useFormik({
    initialValues: {
      role_id: null,
      role_name: '',
      role_description: '',
      role_type: '',
      permissions: null,
      users_assigned: []
    },
    validationSchema: EditRoleValidationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      handleUpdate();
      setSubmitting(false);
    }
  });

  const initialFormValues = useRef(null);

  useEffect(() => {
    if (
      formik.values.role_name !== '' &&
      formik.values.permissions !== null &&
      initialFormValues.current === null
    ) {
      initialFormValues.current = { ...formik.values };
    }

    if (initialFormValues.current !== null) {
      const hasFormChanged = !isEqual(initialFormValues.current, formik.values);

      if (hasFormChanged) {
        setEnableSubmit(true);
      } else {
        setEnableSubmit(false);
      }
    }
  }, [formik.values]);

  const handleClose = () => {
    setShowEditRoleModal(false);
    setEnableSubmit(false);
    setActionsEnabled({});
    formik.resetForm();
  };

  const handleUpdate = async () => {
    const usersAssigned = formik.values.users_assigned;
    const newPermissions = formik.values.permissions;
    try {
      const res = await requestApi({
        url: `/api/permissions/update`,
        params: {
          ...formik.values,
          forAdmin
        },
        navigate
      });

      queryClient.invalidateQueries(['getRoleList']);
      queryClient.invalidateQueries(['getRoleDescriptions']);
      queryClient.invalidateQueries(['getRole']);
      queryClient.fetchQuery(['permissions'], getPermissions);
      handleClose();
      const { code } = res;
      if (code === 0) {
        showAlert({ title: 'Role created and assigned to users', color: 'success' });
        if (usersAssigned.includes(currentUserId)) {
          //Update recoil permissions
          setUserPermissions(newPermissions);
        }
      } else {
        showAlert({ title: "Couldn't create role", color: 'danger' });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  const { id: practiceId } = useParams();
  return (
    <EditRoleContext.Provider
      value={{
        formik,
        setShowEditRoleModal,
        showEditRoleModal,
        handleClose,
        practiceId,
        enableSubmit,
        setEnableSubmit,
        actionsEnabled,
        setActionsEnabled,
        actionDependents,
        setActionDependents,
        actionDependencies,
        setActionDependencies,
        dependencyOfModal,
        setDependencyOfModal,
        showModal,
        setShowModal
      }}>
      {children}
    </EditRoleContext.Provider>
  );
};
